import dayjs from 'dayjs';
import { formatters } from 'components/DashboardNew/config';

/**
 * Compute the selected/highlight index for charts based on theme.selection
 * Falls back to:
 *  - theme.highlightIndex
 *  - theme.highlightX
 *  - max value on Y axis
 */
export function getSelectionIndex({ names = [], values = [], theme = {} } = {}) {
    // 1) Explicit controls
    if (typeof theme?.highlightIndex === 'number') return theme.highlightIndex;

    if (typeof theme?.highlightX !== 'undefined') {
        const idx = names.findIndex(x => String(x).toLowerCase() === String(theme.highlightX).toLowerCase());
        if (idx >= 0) return idx;
    }

    // 2) Selection policy
    const sel = theme?.selection || null;
    if (sel) {
        const toNumber = (v) => {
            const num = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(num) ? num : NaN;
        };
        const findExtremeIndex = (arr, mode) => {
            if (!Array.isArray(arr) || arr.length === 0) return -1;
            let idx = -1; let best = mode === 'min' ? Infinity : -Infinity;
            arr.forEach((v, i) => {
                const num = toNumber(v);
                if (!Number.isFinite(num)) return;
                if ((mode === 'min' && num < best) || (mode === 'max' && num > best)) {
                    best = num; idx = i;
                }
            });
            return idx;
        };
        const applyLabelFormatter = (label) => {
            const formatterKey = sel?.labelFormatter || theme?.xAxisKeyFormatter;
            if (typeof formatterKey === 'string' && typeof formatters?.[formatterKey] === 'function') {
                try { return formatters[formatterKey](label); } catch (_e) { }
            }
            return label;
        };
        const mode = sel?.mode;
        if (mode === 'extreme') {
            const axis = sel?.axis === 'x' ? 'x' : 'y';
            const which = sel?.type === 'min' ? 'min' : 'max';
            if (axis === 'y') return findExtremeIndex(values, which);
            const xNums = names.map(toNumber);
            return findExtremeIndex(xNums, which);
        }
        if (mode === 'today') {
            const unit = sel?.unit || 'weekday';
            let label;
            if (unit === 'weekday') label = dayjs().format(sel?.format || 'ddd');
            else if (unit === 'month') label = dayjs().format(sel?.format || 'MMM');
            else if (unit === 'month-name') label = dayjs().format(sel?.format || 'MMMM');
            else if (unit === 'date') label = dayjs().format(sel?.format || 'D');
            else if (unit === 'year') label = dayjs().format(sel?.format || 'YYYY');
            else label = dayjs().format(sel?.format || 'ddd');
            if (sel?.map && typeof sel.map === 'object' && sel.map !== null) {
                label = sel.map[label] ?? label;
            }
            label = applyLabelFormatter(label);
            const idx = names.findIndex(n => String(n).toLowerCase() === String(label).toLowerCase());
            if (idx >= 0) return idx;
        }
        if (mode === 'position') {
            if (typeof sel?.index === 'number') return sel.index;
            const from = sel?.from === 'last' ? 'last' : 'first';
            const nth = typeof sel?.nth === 'number' ? sel.nth : 1;
            const len = names.length;
            return from === 'last' ? Math.max(0, len - nth) : Math.max(0, nth - 1);
        }
        if (mode === 'xMatch') {
            const target = applyLabelFormatter(sel?.x);
            const idx = names.findIndex(n => String(n).toLowerCase() === String(target).toLowerCase());
            if (idx >= 0) return idx;
        }
        if (mode === 'today-weekday-index') {
            const offset = Number(sel?.offset) || 0; // default 0 = Sunday start
            const rawIdx = dayjs().day(); // 0 = Sun, 1 = Mon, ... 6 = Sat
            const idx = (rawIdx - offset + 7) % 7;
            if (idx >= 0) return idx;
        }
    }
    // 3) Default: max value on Y
    let idx = -1; let max = -Infinity;
    values.forEach((v, i) => { if (typeof v === 'number' && v > max) { max = v; idx = i; } });
    return idx;
}

